import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { supabase } from '../auth/supabaseClient';
import { SIGNUP_COMPLETED_EVENT } from '../auth/signupCompletion';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://helpmediy.org:3001/api';

const DEFAULT_RAFFLE_SETTINGS = {
  videoUrl: '',
  descriptions: {
    youtube: 'Subscribe on YouTube and connect your channel to enter.',
    instagram:
      'Follow @helpmediy_offical and DM #raffle plus your account email.',
    website: 'Create an account on this site to receive a raffle entry.',
    facebook:
      'Follow the Help Me DIY Facebook page and DM #raffle plus your account email.',
    purchase: 'Complete payment to receive purchase raffle entries.',
  },
  sponsor: {
    enabled: false,
    name: '',
    logoUrl: '',
    instructions: '',
  },
  purchase: {
    entries: 1,
    priceUsd: 5,
  },
};

const toYouTubeEmbedUrl = (rawUrl) => {
  const value = String(rawUrl || '').trim();
  if (!value) return '';
  try {
    const parsed = new URL(value);
    if (parsed.hostname.includes('youtu.be')) {
      const id = parsed.pathname.replace('/', '').trim();
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
    if (parsed.hostname.includes('youtube.com')) {
      if (parsed.pathname.startsWith('/embed/')) {
        return value;
      }
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }
  } catch (err) {
    return '';
  }
  return '';
};

const Raffle = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestError, setRequestError] = useState('');
  const [requesting, setRequesting] = useState('');
  const [checkingSignup, setCheckingSignup] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchaseStatusMessage, setPurchaseStatusMessage] = useState('');
  const [purchaseStatusType, setPurchaseStatusType] = useState('success');
  const [raffleSettings, setRaffleSettings] = useState(DEFAULT_RAFFLE_SETTINGS);
  const entriesRevision = useRef(0);

  const loadEntries = useCallback(async () => {
    const revision = ++entriesRevision.current;
    if (!user) {
      setEntries([]);
      setRequests([]);
      return;
    }
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) return;
    const [entriesRes, requestsRes] = await Promise.all([
      fetch(`${API_BASE}/entries/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${API_BASE}/entry-requests/me`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (entriesRes.ok) {
      const payload = await entriesRes.json();
      if (revision === entriesRevision.current) setEntries(payload || []);
    }
    if (requestsRes.ok) {
      const payload = await requestsRes.json();
      if (revision === entriesRevision.current) setRequests(payload.requests || []);
    }
  }, [user]);

  useEffect(() => {
    loadEntries();
    const onCompleted = (event) => {
      if (event.detail?.userId === user?.id) loadEntries();
    };
    window.addEventListener(SIGNUP_COMPLETED_EVENT, onCompleted);
    return () => {
      entriesRevision.current += 1;
      window.removeEventListener(SIGNUP_COMPLETED_EVENT, onCompleted);
    };
  }, [loadEntries, user?.id]);

  useEffect(() => {
    const loadRaffleSettings = async () => {
      try {
        const res = await fetch(`${API_BASE}/raffle/settings`);
        if (!res.ok) return;
        const payload = await res.json();
        setRaffleSettings({
          videoUrl: payload?.videoUrl || '',
          descriptions: {
            youtube:
              payload?.descriptions?.youtube ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.youtube,
            instagram:
              payload?.descriptions?.instagram ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.instagram,
            website:
              payload?.descriptions?.website ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.website,
            facebook:
              payload?.descriptions?.facebook ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.facebook,
            purchase:
              payload?.descriptions?.purchase ||
              DEFAULT_RAFFLE_SETTINGS.descriptions.purchase,
          },
          sponsor: {
            enabled:
              payload?.sponsor?.enabled ??
              Boolean(
                payload?.sponsor?.name ||
                  payload?.sponsor?.logoUrl ||
                  payload?.sponsor?.instructions
              ),
            name: payload?.sponsor?.name || '',
            logoUrl: payload?.sponsor?.logoUrl || '',
            instructions: payload?.sponsor?.instructions ?? '',
          },
          purchase: {
            entries: Number(payload?.purchase?.entries) || 1,
            priceUsd:
              Number(payload?.purchase?.priceUsd) ||
              DEFAULT_RAFFLE_SETTINGS.purchase.priceUsd,
          },
        });
      } catch (err) {
        // Use defaults if settings are unavailable.
      }
    };
    loadRaffleSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const purchaseState = params.get('purchase');
    const clearParams = () => {
      params.delete('purchase');
      params.delete('session_id');
      const search = params.toString();
      const nextUrl = `${window.location.pathname}${search ? `?${search}` : ''}`;
      window.history.replaceState({}, '', nextUrl);
    };

    const confirmPurchase = async () => {
      if (purchaseState !== 'success') return;
      const sessionId = params.get('session_id');
      if (!sessionId) {
        setPurchaseStatusType('success');
        setPurchaseStatusMessage(
          'Payment complete. Your purchase entry is processing.'
        );
        clearParams();
        return;
      }
      if (!user) return;
      try {
        const { data } = await supabase.auth.getSession();
        const token = data.session?.access_token;
        if (!token) throw new Error('Missing session');
        const res = await fetch(
          `${API_BASE}/payments/checkout/raffle-purchase/confirm`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ sessionId }),
          }
        );
        if (!res.ok) throw new Error('Payment confirmation failed.');
        const payload = await res.json();
        if (payload?.entry?._id) {
          setEntries((prev) => {
            const others = prev.filter((entry) => entry._id !== payload.entry._id);
            return [...others, payload.entry];
          });
        } else {
          await loadEntries();
        }
        setPurchaseStatusType('success');
        setPurchaseStatusMessage(
          payload?.alreadyApplied
            ? 'Payment was already confirmed. Your purchase entry is active.'
            : 'Payment complete. Your purchase entry has been recorded.'
        );
      } catch (err) {
        setPurchaseStatusType('error');
        setPurchaseStatusMessage(
          err.message || 'Payment confirmation failed. Please refresh and try again.'
        );
      } finally {
        clearParams();
      }
    };

    if (purchaseState === 'cancel') {
      setPurchaseStatusType('error');
      setPurchaseStatusMessage('Payment was canceled. You can try again anytime.');
      clearParams();
      return;
    }
    confirmPurchase();
  }, [loadEntries, user]);

  const requestEntry = async (method, details = {}) => {
    if (!user) {
      setRequestError('Please sign in to request an entry.');
      return;
    }
    try {
      setRequesting(method);
      setRequestError('');
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Missing session');
      const res = await fetch(`${API_BASE}/entry-requests`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ method, details }),
      });
      if (!res.ok) throw new Error('Request failed');
      const payload = await res.json();
      setRequests((prev) => {
        const existing = prev.filter((req) => req.method !== payload.method);
        return [...existing, payload];
      });
    } catch (err) {
      setRequestError(err.message || 'Failed to request entry.');
    } finally {
      setRequesting('');
    }
  };

  const claimWebsiteEntry = async () => {
    if (!user) return;
    try {
      setCheckingSignup(true);
      setRequestError('');
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Missing session');
      const res = await fetch(`${API_BASE}/entries/website-claim`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error('Failed to claim website entry.');
      const entry = await res.json();
      setEntries((prev) => [...prev, entry]);
    } catch (err) {
      setRequestError(err.message || 'Failed to claim website entry.');
    } finally {
      setCheckingSignup(false);
    }
  };

  const entryMethods = useMemo(() => {
    const methods = new Set(entries.map((entry) => entry.method));
    return methods;
  }, [entries]);

  const hasEntry = (method) => entryMethods.has(method);
  const hasPendingRequest = (method) =>
    requests.some((req) => req.method === method && req.status === 'pending');
  const hasApprovedRequest = (method) =>
    requests.some((req) => req.method === method && req.status === 'approved');

  const openExternal = (url) => {
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      window.location.href = url;
    }
  };

  const checkoutPurchaseEntry = async () => {
    if (!user) {
      setRequestError('Please sign in to continue to payment.');
      return;
    }
    try {
      setIsPurchasing(true);
      setRequestError('');
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Missing session');
      const res = await fetch(`${API_BASE}/payments/checkout/raffle-purchase`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) throw new Error('Failed to create checkout session.');
      const payload = await res.json();
      if (!payload?.url) throw new Error('Missing checkout URL.');
      window.location.href = payload.url;
    } catch (err) {
      setRequestError(err.message || 'Payment failed');
    } finally {
      setIsPurchasing(false);
    }
  };

  return (
    <div className="raffle">
      <div className="raffle-hero">
        <div>
          <h2>Raffle</h2>
          <p>Enter the current raffle with multiple entries!</p>
        </div>
        {toYouTubeEmbedUrl(raffleSettings.videoUrl) && (
          <div className="raffle-hero-video">
            <iframe
              title="Raffle video"
              src={toYouTubeEmbedUrl(raffleSettings.videoUrl)}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        )}
      </div>
      <div className="raffle-cards">
        <div className="raffle-card">
          {hasEntry('YouTube') && <span className="raffle-check">✓</span>}
          <h3>YouTube Entry</h3>
          <p>{raffleSettings.descriptions.youtube}</p>
          {hasEntry('YouTube') ? (
            <span className="raffle-link-button disabled">Entered</span>
          ) : (
            <Link to="/raffle/youtube" className="raffle-link-button">
              Enter via YouTube
            </Link>
          )}
        </div>
        <div className="raffle-card">
          {(hasEntry('Instagram') || hasApprovedRequest('Instagram')) && (
            <span className="raffle-check">✓</span>
          )}
          {hasPendingRequest('Instagram') && (
            <span className="raffle-check pending">○</span>
          )}
          <h3>Instagram Entry</h3>
          <p>{raffleSettings.descriptions.instagram}</p>
          {hasEntry('Instagram') || hasApprovedRequest('Instagram') ? (
            <span className="raffle-link-button disabled">Entered</span>
          ) : hasPendingRequest('Instagram') ? (
            <span className="raffle-link-button pending">Requested</span>
          ) : (
            <a
              className="raffle-link-button"
              href="https://www.instagram.com/helpmediy_official/"
              target="_blank"
              rel="noreferrer noopener"
              onClick={(event) => {
                event.preventDefault();
                openExternal('https://www.instagram.com/helpmediy_official/');
              }}
            >
              Visit Instagram Page
            </a>
          )}
          {!hasEntry('Instagram') &&
            !hasApprovedRequest('Instagram') &&
            !hasPendingRequest('Instagram') && (
              <button
                type="button"
                className="raffle-link-button secondary"
                onClick={() => requestEntry('Instagram')}
                disabled={requesting === 'Instagram'}
              >
                {requesting === 'Instagram' ? 'Requesting...' : 'Request Entry'}
              </button>
            )}
        </div>
        <div className="raffle-card">
          {hasEntry('Website') && <span className="raffle-check">✓</span>}
          <h3>Website Signup Entry</h3>
          <p>{raffleSettings.descriptions.website}</p>
          {hasEntry('Website') ? (
            <span className="raffle-link-button disabled">Entered</span>
          ) : user ? (
            <button
              type="button"
              className="raffle-link-button"
              onClick={claimWebsiteEntry}
              disabled={checkingSignup}
            >
              {checkingSignup ? 'Checking...' : 'Claim Entry'}
            </button>
          ) : (
            <Link to="/login?mode=signup&signupSource=raffle&returnTo=%2Fraffle" className="raffle-link-button">
              Create Account
            </Link>
          )}
        </div>
        <div className="raffle-card">
          {(hasEntry('FaceBook') || hasApprovedRequest('FaceBook')) && (
            <span className="raffle-check">✓</span>
          )}
          {hasPendingRequest('FaceBook') && (
            <span className="raffle-check pending">○</span>
          )}
          <h3>Facebook Entry</h3>
          <p>{raffleSettings.descriptions.facebook}</p>
          {hasEntry('FaceBook') || hasApprovedRequest('FaceBook') ? (
            <span className="raffle-link-button disabled">Entered</span>
          ) : hasPendingRequest('FaceBook') ? (
            <span className="raffle-link-button pending">Requested</span>
          ) : (
            <a
              className="raffle-link-button"
              href="https://www.facebook.com/HelpMeDIY"
              target="_blank"
              rel="noreferrer noopener"
              onClick={(event) => {
                event.preventDefault();
                openExternal('https://www.facebook.com/HelpMeDIY');
              }}
            >
              Visit Facebook Page
            </a>
          )}
          {!hasEntry('FaceBook') &&
            !hasApprovedRequest('FaceBook') &&
            !hasPendingRequest('FaceBook') && (
              <button
                type="button"
                className="raffle-link-button secondary"
                onClick={() => requestEntry('FaceBook')}
                disabled={requesting === 'FaceBook'}
              >
                {requesting === 'FaceBook' ? 'Requesting...' : 'Request Entry'}
              </button>
            )}
        </div>
        {raffleSettings.sponsor.enabled && (
          <div className="raffle-card">
            {(hasEntry('Sponsor') || hasApprovedRequest('Sponsor')) && (
              <span className="raffle-check">✓</span>
            )}
            {hasPendingRequest('Sponsor') && (
              <span className="raffle-check pending">○</span>
            )}
            <h3>
              {raffleSettings.sponsor.name || 'Sponsor'}
            </h3>
            {raffleSettings.sponsor.logoUrl && (
              <img
                src={raffleSettings.sponsor.logoUrl}
                alt={`${raffleSettings.sponsor.name || 'Sponsor'} logo`}
                className="raffle-sponsor-logo"
              />
            )}
            {raffleSettings.sponsor.instructions && (
              <p>{raffleSettings.sponsor.instructions}</p>
            )}
            {hasEntry('Sponsor') || hasApprovedRequest('Sponsor') ? (
              <span className="raffle-link-button disabled">Entered</span>
            ) : hasPendingRequest('Sponsor') ? (
              <span className="raffle-link-button pending">Requested</span>
            ) : (
              <button
                type="button"
                className="raffle-link-button"
                onClick={() =>
                  requestEntry('Sponsor', { sponsor: raffleSettings.sponsor.name })
                }
                disabled={requesting === 'Sponsor'}
              >
                {requesting === 'Sponsor' ? 'Requesting...' : 'Request Entry'}
              </button>
            )}
          </div>
        )}
        <div className="raffle-card">
          {hasEntry('Purchase') && <span className="raffle-check">✓</span>}
          <h3>Purchase Entry</h3>
          <p>{raffleSettings.descriptions.purchase}</p>
          <p>
            Get <strong>{raffleSettings.purchase.entries}</strong> raffle entries
            for <strong>${Number(raffleSettings.purchase.priceUsd).toFixed(2)}</strong>.
          </p>
          {hasEntry('Purchase') ? (
            <span className="raffle-link-button disabled">Entered</span>
          ) : !user ? (
            <Link to="/login" className="raffle-link-button">
              Sign In to Pay
            </Link>
          ) : (
            <button
              type="button"
              className="raffle-link-button"
              onClick={checkoutPurchaseEntry}
              disabled={isPurchasing}
            >
              {isPurchasing ? 'Starting Checkout...' : 'Pay with Swipe'}
            </button>
          )}
        </div>
      </div>
      {purchaseStatusMessage && (
        <div
          className={
            purchaseStatusType === 'error' ? 'error-banner' : 'success-banner'
          }
        >
          {purchaseStatusMessage}
        </div>
      )}
      {requestError && <div className="error-banner">{requestError}</div>}
    </div>
  );
};

export default Raffle;
